import {
  formatQuery,
  generateID,
  prepareRuleGroup,
  type RuleGroupType,
  type RuleGroupTypeAny,
  type RuleType
} from 'react-querybuilder';
import type { AudienceDefinitionGenderEnum, AudienceFilterKey, AudienceFilters } from '../types';
import {
  AUDIENCE_FIELDS,
  type AudienceFieldConfig,
  type AudienceFieldName,
  audienceField,
  fieldForFlatKey,
  isNegatableField,
  NEGATED_OPERATOR,
  operatorForFlatKey,
  rangeBounds,
  ruleFlatKeys,
  ruleLabel,
  ruleSlot,
  toNumber
} from './audienceFields';
import { applyFilterPatch, EMPTY_FILTERS } from './filters';

/* Converts between the react-querybuilder tree the builder edits and the flat filter set proxima-audiences takes.
   The tree is a flat AND list with at most one rule per slot.
   A slot is a field, or one side of a negatable field such as States or Bought from brands. */

/**
 * A rule, or the top-level group, that contributes nothing to the flat filters.
 *
 * - `reason` says why it is left out; proposed columns carry `PROPOSED_REASON`.
 * - `incomplete` is set on a proposed rule whose value is missing or malformed, with the value's reason.
 */
export type DroppedRule = {
  ruleId: string | null;
  field: string;
  reason: string;
  incomplete?: string;
};

export type TreeToFlatResult = {
  filters: AudienceFilters;
  dropped: DroppedRule[];
  /** Complete proposed rules' values under their proposed keys; never sent to preview or count. */
  proposedValues: Record<string, unknown>;
};

/* Why a rule's value writes nothing: missing or malformed, which the rule's validation error also reports */
export const VALUE_REASONS = {
  bothBounds: 'needs both bounds',
  number: 'needs a number',
  atLeastOne: 'needs at least one value',
  gender: 'must be any, male, or female',
  tristate: 'must be has or has not',
  choice: 'pick one of the options'
} as const;

const VALUE_REASON_SET: ReadonlySet<string> = new Set(Object.values(VALUE_REASONS));

/* Gender "Any" is a rule with no filter: valid, but it writes nothing */
export const ANY_GENDER = 'any';

/* Proposed rules are listed with this reason; they never block count or create */
export const PROPOSED_REASON = 'proposed';

/**
 * Tree to flat filters.
 *
 * - An OR combinator or NOT on the top-level group adds a blocking dropped entry; its rules still map.
 * - Nested groups and muted rules are dropped.
 * - A rule with a missing or malformed value is dropped with the reason, and its field stays unset.
 * - A second rule in the same slot is dropped; the first one wins.
 *   A negatable field keeps one include rule and one exclude rule.
 * - A proposed column is dropped with the reason "proposed".
 *   Its value goes to `proposedValues` when complete, and its value's reason goes to `incomplete` otherwise.
 * - `rankColumn` is derived from the size cap, as the service does.
 */
export const treeToFlat = (query: RuleGroupTypeAny): TreeToFlatResult => {
  const dropped: DroppedRule[] = [];
  let filters: AudienceFilters = EMPTY_FILTERS;
  let proposedValues: Record<string, unknown> = {};
  const seenSlots = new Set<string>();
  const seenFields = new Set<string>();

  if ('combinator' in query && query.combinator !== 'and')
    dropped.push({
      ruleId: query.id ?? null,
      field: '',
      reason: `combinator ${query.combinator} is not supported`
    });
  if (query.not)
    dropped.push({ ruleId: query.id ?? null, field: '', reason: 'NOT is not supported' });

  for (const item of query.rules) {
    if (typeof item === 'string') continue;
    if ('rules' in item) {
      dropped.push({
        ruleId: item.id ?? null,
        field: '',
        reason: 'nested groups are not supported'
      });
      continue;
    }
    const rule = item as RuleType;
    const ruleId = rule.id ?? null;
    const field = audienceField(rule.field);
    if (!field) {
      dropped.push({ ruleId, field: rule.field, reason: `unknown field ${rule.field}` });
      continue;
    }
    if (rule.muted) {
      dropped.push({ ruleId, field: rule.field, reason: 'muted' });
      continue;
    }
    const slot = ruleSlot(field, rule.operator);
    if (seenSlots.has(slot)) {
      dropped.push({
        ruleId,
        field: rule.field,
        reason: `${ruleLabel(field, rule.operator)} already has a rule`
      });
      continue;
    }
    /* The builder's own controls can produce a tree with both sides of an exclusion */
    const conflict = (field.excludes ?? []).find(name => seenFields.has(name));
    if (conflict) {
      const other = audienceField(conflict)?.label ?? conflict;
      dropped.push({
        ruleId,
        field: rule.field,
        reason: `${field.label} cannot be combined with ${other}`
      });
      continue;
    }
    seenSlots.add(slot);
    seenFields.add(field.name);
    const result = ruleToPatch(field, rule);
    if (field.backendSupport === 'proposed') {
      if ('patch' in result) {
        dropped.push({ ruleId, field: rule.field, reason: PROPOSED_REASON });
        proposedValues = { ...proposedValues, ...result.patch };
      } else
        dropped.push({
          ruleId,
          field: rule.field,
          reason: PROPOSED_REASON,
          incomplete: result.reason
        });
    } else if ('reason' in result)
      dropped.push({ ruleId, field: rule.field, reason: result.reason });
    else filters = { ...filters, ...(result.patch as Partial<AudienceFilters>) };
  }

  return { filters: applyFilterPatch(filters, {}), dropped, proposedValues };
};

type RuleShape = Omit<RuleType, 'id'>;

const rangeRule = (
  field: AudienceFieldName,
  min: number | null,
  max: number | null
): RuleShape | null => {
  if (min != null && max != null) return { field, operator: 'between', value: [min, max] };
  if (min != null) return { field, operator: '>=', value: min };
  if (max != null) return { field, operator: '<=', value: max };
  return null;
};

/**
 * The rules a field would carry for these filters, empty when the field is unset.
 *
 * A negatable field gets one rule per non-empty side, include first.
 */
export const rulesForField = (
  field: AudienceFieldConfig,
  filters: AudienceFilters
): RuleShape[] => {
  /* Proposed columns have no flat keys, so saved filters never produce them */
  if (field.backendSupport === 'proposed') return [];
  switch (field.editor) {
    case 'range':
    case 'percentRange': {
      const rule = rangeRule(field.name, filters[field.flatKeys[0]], filters[field.flatKeys[1]]);
      return rule ? [rule] : [];
    }
    case 'number':
    case 'lookback':
    case 'gender':
    case 'tristate':
    case 'choice': {
      const value = filters[field.flatKeys[0]];
      return value == null ? [] : [{ field: field.name, operator: field.defaultOperator, value }];
    }
    case 'states':
    case 'brandIds':
      return field.flatKeys.flatMap(key => {
        const list = filters[key];
        return list.length > 0
          ? [{ field: field.name, operator: operatorForFlatKey(field, key), value: [...list] }]
          : [];
      });
    /* The other list editors: options and chips */
    default: {
      const list = filters[field.flatKeys[0]];
      return list.length > 0
        ? [{ field: field.name, operator: field.defaultOperator, value: [...list] }]
        : [];
    }
  }
};

/** Flat filters to a tree, one rule per filled slot, in field-config order, with fresh ids. */
export const flatToTree = (filters: AudienceFilters): RuleGroupType =>
  prepareRuleGroup({
    combinator: 'and',
    rules: AUDIENCE_FIELDS.flatMap(field => rulesForField(field, filters) as RuleType[])
  });

export const EMPTY_TREE: RuleGroupType = flatToTree(EMPTY_FILTERS);

/**
 * SQL for the rules that map to filters.
 * Incomplete rules are left out, as they are on save.
 */
export const completeRulesSql = (query: RuleGroupType): string | null => {
  const complete = flatToTree(treeToFlat(query).filters);
  return complete.rules.length > 0 ? rulesToSql(complete) : null;
};

/* Numbers are quoted as strings unless parseNumbers is on */
export const rulesToSql = (query: RuleGroupType): string =>
  formatQuery(query, { format: 'sql', parseNumbers: true });

/**
 * Applies a flat-filter patch, such as a chip's clear patch, to the tree.
 *
 * Only the rules owning the patched keys change: each is rewritten in place, keeping its id, or removed when the patch leaves its field unset.
 * Incomplete rules elsewhere are untouched.
 */
export const patchTree = (query: RuleGroupType, patch: Partial<AudienceFilters>): RuleGroupType => {
  const next = applyFilterPatch(treeToFlat(query).filters, patch);
  const touched = new Map<string, AudienceFieldConfig>();
  for (const key of Object.keys(patch) as AudienceFilterKey[]) {
    const field = fieldForFlatKey(key);
    if (field) touched.set(ruleSlot(field, operatorForFlatKey(field, key)), field);
  }
  let rules = query.rules;
  touched.forEach((field, slot) => {
    const inSlot = (rule: { operator: string }) => ruleSlot(field, rule.operator) === slot;
    const replacement = rulesForField(field, next).find(inSlot);
    const existing = rules.find(
      item => isRule(item) && item.field === field.name && inSlot(item)
    ) as RuleType | undefined;
    if (!replacement) rules = rules.filter(item => item !== existing);
    else if (existing)
      rules = rules.map(item => (item === existing ? { ...existing, ...replacement } : item));
    else rules = [...rules, { ...replacement, id: generateID() }];
  });
  return { ...query, rules };
};

/**
 * Appends a rule for the field with its default operator and value; no-op when the field has any rule.
 *
 * Rules for fields the new one `excludes` are removed first, e.g. Banded age replaces Age.
 */
export const addFieldRule = (query: RuleGroupType, name: AudienceFieldName): RuleGroupType => {
  const field = audienceField(name);
  if (!field || fieldsInTree(query).has(name)) return query;
  const rule: RuleType = {
    id: generateID(),
    field: name,
    operator: field.defaultOperator,
    value: structuredClone(field.defaultValue)
  };
  const kept = (field.excludes ?? []).reduce(removeFieldRule, query);
  return { ...kept, rules: [...kept.rules, rule] };
};

/** Removes every rule of the field, both sides of a negatable field included. */
export const removeFieldRule = (query: RuleGroupType, name: string): RuleGroupType => ({
  ...query,
  rules: query.rules.filter(item => !isRule(item) || item.field !== name)
});

/** Field names that have a rule in the tree, for the column mapper's checkboxes. */
export const fieldsInTree = (query: RuleGroupType): Set<string> =>
  new Set(query.rules.filter(isRule).map(rule => rule.field));

/**
 * Moves a negatable rule to the other side, keeping its id and value.
 *
 * No-op when the rule is missing, its field is not negatable, or the other side already has a rule.
 */
export const toggleRuleSide = (query: RuleGroupType, ruleId: string): RuleGroupType => {
  const rule = query.rules.find(item => isRule(item) && item.id === ruleId) as RuleType | undefined;
  const field = audienceField(rule?.field ?? '');
  if (!rule || !isNegatableField(field)) return query;
  const operator = oppositeOperator(rule.operator);
  if (slotTaken(query, field, operator, ruleId)) return query;
  return {
    ...query,
    rules: query.rules.map(item => (item === rule ? { ...rule, operator } : item))
  };
};

/**
 * Adds an empty rule on the other side of a negatable rule, right after it.
 *
 * No-op when the rule is missing, its field is not negatable, or the other side already has a rule.
 */
export const addOppositeRule = (query: RuleGroupType, ruleId: string): RuleGroupType => {
  const index = query.rules.findIndex(item => isRule(item) && item.id === ruleId);
  const rule = query.rules[index] as RuleType | undefined;
  const field = audienceField(rule?.field ?? '');
  if (!rule || !isNegatableField(field)) return query;
  const operator = oppositeOperator(rule.operator);
  if (slotTaken(query, field, operator)) return query;
  const added: RuleType = { id: generateID(), field: field.name, operator, value: [] };
  return {
    ...query,
    rules: [...query.rules.slice(0, index + 1), added, ...query.rules.slice(index + 1)]
  };
};

/** The other side's operator of a negatable field: `in` for `notIn`, `notIn` otherwise. */
export const oppositeOperator = (operator: string): 'in' | 'notIn' =>
  operator === NEGATED_OPERATOR ? 'in' : NEGATED_OPERATOR;

/** Whether a rule other than `exceptId` sits in the field's slot for this operator. */
export const slotTaken = (
  query: RuleGroupType,
  field: AudienceFieldConfig,
  operator: string,
  exceptId?: string
): boolean => {
  const slot = ruleSlot(field, operator);
  return query.rules.some(
    item =>
      isRule(item) &&
      item.id !== exceptId &&
      item.field === field.name &&
      ruleSlot(field, item.operator) === slot
  );
};

/** Dropped rules that stop count and create: everything except proposed columns. */
export const blockingDropped = (dropped: DroppedRule[]): DroppedRule[] =>
  dropped.filter(item => item.reason !== PROPOSED_REASON);

/**
 * The reason to show on each rule row, keyed by rule id.
 *
 * - A blocking entry shows its `reason`.
 * - A proposed entry shows its `incomplete` reason, and nothing when its value is complete.
 * - Entries without a rule id are left out.
 */
export const droppedReasonsByRule = (dropped: DroppedRule[]): Record<string, string> => {
  const reasons: Record<string, string> = {};
  for (const item of dropped) {
    const reason = item.reason === PROPOSED_REASON ? item.incomplete : item.reason;
    if (item.ruleId != null && reason && !(item.ruleId in reasons)) reasons[item.ruleId] = reason;
  }
  return reasons;
};

/** Whether a dropped reason is one of `VALUE_REASONS`. */
export const isValueReason = (reason: string): boolean => VALUE_REASON_SET.has(reason);

type RulePatch = { patch: Record<string, unknown> } | { reason: string };

/* One rule's contribution under the field's keys (flat keys, or proposed keys), or why it has none */
const ruleToPatch = (field: AudienceFieldConfig, rule: RuleType): RulePatch => {
  const { operator, value } = rule;
  if (!field.operators.includes(operator as never))
    return { reason: `operator ${operator} is not allowed for ${field.label}` };
  const keys: readonly string[] =
    field.backendSupport === 'proposed' ? field.proposedKeys : field.flatKeys;
  const [firstKey, secondKey] = keys;

  switch (field.editor) {
    case 'range':
    case 'percentRange': {
      if (operator === 'between') {
        const [low, high] = rangeBounds(value).map(toNumber);
        if (low === undefined || high === undefined) return { reason: VALUE_REASONS.bothBounds };
        return { patch: { [firstKey]: low, [secondKey]: high } };
      }
      const bound = toNumber(value);
      if (bound === undefined) return { reason: VALUE_REASONS.number };
      return { patch: { [operator === '>=' ? firstKey : secondKey]: bound } };
    }
    case 'number':
    case 'lookback': {
      const number = toNumber(value);
      if (number === undefined) return { reason: VALUE_REASONS.number };
      return { patch: { [firstKey]: number } };
    }
    case 'states':
    case 'options':
    case 'chips':
    case 'brandIds': {
      const list = toList(value);
      if (!list?.length) return { reason: VALUE_REASONS.atLeastOne };
      const listKey =
        field.backendSupport === 'proposed' ? firstKey : ruleFlatKeys(field, operator)[0];
      return { patch: { [listKey]: list } };
    }
    case 'gender':
      if (value === ANY_GENDER) return { patch: {} };
      if (value !== 'male' && value !== 'female') return { reason: VALUE_REASONS.gender };
      return { patch: { gender: value as AudienceDefinitionGenderEnum } };
    case 'tristate': {
      const flag = toBoolean(value);
      if (flag === undefined) return { reason: VALUE_REASONS.tristate };
      return { patch: { [firstKey]: flag } };
    }
    case 'choice': {
      const valid = field.options.some(option => option.value === value);
      if (!valid) return { reason: VALUE_REASONS.choice };
      return { patch: { [firstKey]: value } };
    }
    default:
      return { reason: 'unsupported editor' };
  }
};

const toList = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value !== '')
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  return undefined;
};

const toBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

const isRule = (item: RuleGroupType['rules'][number]): item is RuleType =>
  typeof item !== 'string' && !('rules' in item);
